// src/pages/super-admin/Clinics/ClinicList.tsx
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import CreateClinic from "./CreateClinic";
import EditClinic from "./EditClinic";
import {
  useListAllClinicsQuery,
  useDeleteClinicMutation,
  useGetClinicByIdQuery,
  type Clinic,
} from "@/api/clinicApi";
import { useListVendorsQuery } from "@/api/vendorApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Plus, Edit2, Trash2, Hospital } from "lucide-react";

export default function ClinicList() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");
  const [vendorFilter, setVendorFilter] = useState<number | "ALL">("ALL");

  const {
    data: allClinics = [],
    isLoading: allClinicsLoading,
    error: allError,
    isError: allIsError,
  } = useListAllClinicsQuery();

  const { data: vendors = [] } = useListVendorsQuery();
  const [deleteClinic] = useDeleteClinicMutation();

  const { data: selectedClinic, isLoading: selectedClinicLoading } =
    useGetClinicByIdQuery(selectedClinicId!, {
      skip: !selectedClinicId,
    });

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this clinic?")) {
      try {
        await deleteClinic(id).unwrap();
        toast.success("Clinic deleted successfully");
      } catch (err) {
        toast.error("Failed to delete clinic");
      }
    }
  };

  const handleEdit = (clinic: Clinic) => {
    setSelectedClinicId(clinic.id);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedClinicId(null);
  };

  const getVendorName = (vendorId: number): string => {
    const vendor = vendors.find((v: any) => v.id === vendorId);
    return vendor?.vendorName || "Unknown Vendor";
  };

  const filteredClinics = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allClinics.filter((clinic) => {
      if (statusFilter !== "ALL" && String(clinic.status || "").toUpperCase() !== statusFilter) {
        return false;
      }
      if (vendorFilter !== "ALL" && clinic.vendorId !== vendorFilter) {
        return false;
      }
      if (!q) {
        return true;
      }

      const vendorName = getVendorName(clinic.vendorId).toLowerCase();
      const haystack = [
        vendorName,
        String(clinic.clinicName || "").toLowerCase(),
        String(clinic.city || "").toLowerCase(),
        String(clinic.state || "").toLowerCase(),
        String(clinic.zipCode || "").toLowerCase(),
        String(clinic.clinicAddress || "").toLowerCase(),
      ].join(" ");

      return haystack.includes(q);
    });
  }, [allClinics, query, statusFilter, vendorFilter, vendors]);

  const vendorOptions = useMemo(() => {
    return [...vendors]
      .map((v: any) => ({ id: Number(v.id), name: String(v.vendorName || "").trim() }))
      .filter((v) => Number.isFinite(v.id) && v.id > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [vendors]);

  if (allClinicsLoading) {
    return (
      <div className="flex justify-center py-8">
        <p className="text-gray-500">Loading clinics...</p>
      </div>
    );
  }

  if (allIsError || allError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-800 font-bold">Error loading clinics</p>
        <p className="text-red-600 text-sm mt-2">{JSON.stringify(allError)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
             <Hospital className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100">Clinics Management</h1>
            <p className="text-slate-500 text-sm font-medium">Manage and configure diagnostic clinic locations.</p>
          </div>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-5 h-11 font-bold shadow-lg shadow-blue-100 dark:shadow-none transition-all active:scale-95">
              <Plus className="w-4 h-4 mr-2" />
              Add Clinic
            </Button>
          </DialogTrigger>
          <DialogContent className="w-full max-w-2xl rounded-2xl border-2">
            <DialogHeader>
              <DialogTitle className="text-xl font-black">Create New Clinic</DialogTitle>
            </DialogHeader>
            <CreateClinic onClose={() => setCreateOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Clinics Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between px-2">
            <CardTitle className="text-lg font-bold">All Clinics ({filteredClinics.length})</CardTitle>
            <div className="flex flex-wrap gap-3 md:items-center">
              <div className="relative group min-w-[300px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                <Input
                  placeholder="Search by clinic/vendor/city/state/zip"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9 h-11 border-slate-200 rounded-xl bg-slate-50/50 focus-visible:ring-blue-500"
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(val) => setStatusFilter(val as typeof statusFilter)}
              >
                <SelectTrigger className="h-11 w-[140px] rounded-xl border-slate-200 bg-slate-50/50 text-slate-700">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={vendorFilter === "ALL" ? "ALL" : String(vendorFilter)}
                onValueChange={(val) => {
                  setVendorFilter(val === "ALL" ? "ALL" : Number(val));
                }}
              >
                <SelectTrigger className="h-11 w-[180px] rounded-xl border-slate-200 bg-slate-50/50 text-slate-700">
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="ALL">All Vendors</SelectItem>
                  {vendorOptions.map((v) => (
                    <SelectItem key={v.id} value={String(v.id)}>
                      {v.name || `Vendor #${v.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredClinics.length === 0 ? (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5.581m0 0H9m5.581 0a2 2 0 100-4 2 2 0 000 4m0 0a2 2 0 100-4 2 2 0 000 4"
                />
              </svg>
              <p className="text-gray-500 font-medium mb-4 mt-4">
                No clinics found
              </p>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600">+ Add First Clinic</Button>
                </DialogTrigger>
              </Dialog>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-slate-100">
              <Table>
                <TableHeader className="bg-slate-50/50">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-bold">Vendor Name</TableHead>
                    <TableHead className="font-bold">Clinic Name</TableHead>
                    <TableHead className="font-bold">Address</TableHead>
                    <TableHead className="font-bold">City</TableHead>
                    <TableHead className="font-bold">State</TableHead>
                    <TableHead className="font-bold">Zip Code</TableHead>
                    <TableHead className="font-bold">Phone</TableHead>
                    <TableHead className="text-center font-bold">Status</TableHead>
                    <TableHead className="text-right font-bold pr-11">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClinics.map((clinic: Clinic) => (
                    <TableRow key={clinic.id} className="hover:bg-slate-50/50 transition-colors group">
                      <TableCell className="font-medium text-slate-700">
                        {getVendorName(clinic.vendorId)}
                      </TableCell>
                      <TableCell className="font-black text-slate-900 dark:text-slate-100">
                        {clinic.clinicName}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[150px] truncate">
                        {clinic.clinicAddress}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{clinic.city}</TableCell>
                      <TableCell className="text-sm text-slate-600">{clinic.state}</TableCell>
                      <TableCell className="text-sm text-slate-600">{clinic.zipCode}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {clinic.contactPhone}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          className={`font-bold border-0 ${
                            clinic.status === "ACTIVE"
                              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                              : clinic.status === "INACTIVE"
                              ? "bg-rose-100 text-rose-700 hover:bg-rose-100"
                              : "bg-amber-100 text-amber-700 hover:bg-amber-100"
                          }`}
                        >
                          {clinic.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(clinic)}
                            className="h-9 w-9 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(clinic.id)}
                            className="h-9 w-9 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={handleEditClose}>
        <DialogContent className="w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Clinic</DialogTitle>
          </DialogHeader>
          {selectedClinicLoading ? (
            <div className="flex justify-center py-8">
              <p className="text-gray-500">Loading clinic details...</p>
            </div>
          ) : selectedClinic ? (
            <EditClinic key={selectedClinic.id} clinic={selectedClinic} onClose={handleEditClose} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
